import React, { useState, useEffect, memo } from "react";

import {
  Row,
  Col,
  Card,
  Tag,
  Spin,
  message,
  Typography,
  Badge,
  Space,
} from "antd";
import {
  AlertOutlined,
  ThunderboltOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { rtdb } from "../firebase";
import { ref, onValue } from "firebase/database";
import { useAuth } from "../context/AuthContext";

const { Text } = Typography;
const diffDate = (lastUpdate) => {
  const currentTimestamp = Date.now(); // Récupère le timestamp actuel en ms

  // Calcul de la différence
  const diffInMs = currentTimestamp - lastUpdate;
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

  // Formatage de la date (ex: "3 février 2026 à 11:33")
  const readableDate = new Date(lastUpdate).toLocaleString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return {
    since: diffInMinutes,
    lastUpdateDate: readableDate,
  };
};

const getNextHalfHourDelay = () => {
  const now = new Date();
  const mm = now.getMinutes();
  let next = new Date(now);
  if (mm < 30) {
    next.setMinutes(30, 0, 0);
  } else {
    next.setHours(next.getHours() + 1, 30, 0, 0);
  }
  return Math.round((next - now) / 60000);
};

const StatusRow = memo(({ label, value, type = "relay", children }) => {
  let statusColor = "#595959";
  let labelTxt = "ok";
  if (type === "relay") {
    const active = value;
    labelTxt = "";
    if (active) {
      statusColor = "var(--color-success)";
      labelTxt = "Active";
    }
  } else {
    if (value === 1) {
      statusColor = "var(--color-error)";
      labelTxt = "VIDE";
    }
    if (value === 2) {
      statusColor = "var(--color-warning)";
      labelTxt = "OK";
    }
    if (value === 3) {
      statusColor = "var(--color-success)";
      labelTxt = "OK";
    }
    if (value === 4) {
      statusColor = "var(--color-error)";
      labelTxt = "ERREUR";
    }
  }
  return (
    <div className="status-row">
      <Text className="status-label">
        {label}
        {children && (
          <span style={{ marginLeft: 8, display: "inline-flex", gap: 4, verticalAlign: "middle" }}>
            {children}
          </span>
        )}
      </Text>
      <Space>
        <Text strong className="status-value">
          {labelTxt}
        </Text>
        <Badge color={statusColor} />
      </Space>
    </div>
  );
});


const Controle = () => {
  const { selectedGreenhouse } = useAuth();
  const [states, setStates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requiredHours, setRequiredHours] = useState([]);
  
  useEffect(() => {
    if (!selectedGreenhouse) {
      // Utiliser setTimeout pour éviter d'appeler setState directement dans l'effet
      setTimeout(() => setLoading(false), 0);
      return;
    }

    // Stream status
    const statesRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/status`);
    const unsubscribeStatus = onValue(
      statesRef,
      (snapshot) => {
        setStates({
          ...snapshot.val(),
          diffDate: diffDate(snapshot.val().timestamp),
        });
        setLoading(false);
      },
      (error) => {
        message.error("Erreur status : " + error.message);
      },
    );

    // Stream requiredHours
    const hoursRef = ref(rtdb, `greenhouses/${selectedGreenhouse}/params/irrigation/requiredHours`);
    const unsubscribeHours = onValue(hoursRef, (snapshot) => {
      if (snapshot.exists()) {
        setRequiredHours(snapshot.val());
      } else {
        setRequiredHours([]);
      }
    });

    // Cleanup les 2 streams
    return () => {
      unsubscribeStatus();
      unsubscribeHours();
    };
  }, [selectedGreenhouse]);

  // Déterminer la prochaine heure d'irrigation
  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const nextHour = requiredHours.length > 0
    ? requiredHours.find((h) => h > currentHour || (h === currentHour && currentMinute === 0))
    : null;


  if (loading)
    return (
      <div className="loading-large">
        <Spin size="large" tip="Connexion à la serre..." />
      </div>
    );
  if (!states) return <p>Données indisponibles.</p>;

  return (
    <div className="page-container">
      <h2 className="page-title">Monitoring</h2>
      {/* <Text type="secondary">
        Visualisation en temps réel du statut de votre insallation.
      </Text> */}

      <Row gutter={[16, 16]} className="row-gutter-top">
        {/* Colonne GAUCHE : État des Actionneurs */}
        <Col xs={24}>
          <Card
            title={
              <span>
                <ThunderboltOutlined
                  style={{ color: "var(--color-primary)", marginRight: 8 }}
                />
                Irrigation
              </span>
            }
            className="card-style"
            styles={{
              header: { color: "var(--text-medium)", background: "var(--bg-card-header)", padding: "var(--card-header-padding)" },
              body: { padding: "var(--card-padding)" }
            }}
          >
            <StatusRow label="Heures d'Irrigation" value={states.relayIrrig}>
              {requiredHours.length > 0 && requiredHours.map((h) => {
                const isNext = h === nextHour;
                const isPast = h < currentHour || (h === currentHour && currentMinute > 0);
                return (
                  <Tag
                    key={h}
                    color={isNext ? "blue" : undefined}
                    style={{
                      background: isNext ? "#1677ff" : undefined,
                      color: isNext ? "#fff" : undefined,
                      border: isNext ? "1px solid #1677ff" : undefined,
                      opacity: isPast ? 0.4 : 1,
                      fontWeight: isNext ? 600 : 400,
                      fontSize: 11,
                      lineHeight: "18px",
                      padding: "0 4px",
                    }}
                  >
                    {String(h).padStart(2, "0")}h
                  </Tag>
                );
              })}
            </StatusRow>
            <div className="description">
              <Text type="secondary" size="small">
                Note : Les pompes sont pilotées par la box BioConnect selon vos
                paramètres.
              </Text>
            </div>
          </Card>
        </Col>

        {/* Colonne DROITE : Réservoir & Sécurité */}
        <Col xs={24}>
          <Card
            title={
              <span>
                <ExperimentOutlined style={{ color: "var(--color-primary)", marginRight: 8 }} />{" "}
                Réservoir
              </span>
            }
            className="card-style"
            styles={{
              header: { color: "var(--text-medium)", background: "var(--bg-card-header)", padding: "var(--card-header-padding)" },
              body: { padding: "var(--card-padding)" }
            }}
          >
            <StatusRow
              label="Niveau Cuve Nutriments"
              value={states.level}
              type="other"
            />
            <StatusRow
              label="Vanne Remplissage Eau"
              value={states.relayFill}
            />
            {states.level === 1 && !states.relayFill && (
              <div className="description" style={{ marginTop: 8 }}>
                <Text type="warning" strong>
                  Remplissage dans : {getNextHalfHourDelay()} min
                </Text>
              </div>
            )}
          </Card>

          <Card
            title={
              <span>
                <AlertOutlined
                  style={{
                    color: states.SYSTEM_LOCK_ERROR ? "#ff4d4f" : "#aaa",
                    marginRight: 8,
                  }}
                />{" "}
                Sécurité
              </span>
            }
            className="card-style"
            style={{
              marginTop: 20,
              borderColor: states.SYSTEM_LOCK_ERROR ? "#ff4d4f" : "var(--border-color)",
            }}
            styles={{
              header: { color: "var(--text-medium)", background: "var(--bg-card-header)", padding: "var(--card-header-padding)" },
              body: { padding: "var(--card-padding)" }
            }}
          >
            <div className="security-status-wrapper">
              {states.level === 4 || states.diffDate.since > 1440 ? (
                <Tag
                  color="error"
                  icon={<WarningOutlined />}
                  className="status-tag"
                >
                  SYSTÈME VERROUILLÉ
                </Tag>
              ) : (
                <Tag
                  color="success"
                  icon={<CheckCircleOutlined />}
                  className="status-tag"
                >
                  OPÉRATIONNEL
                </Tag>
              )}
            </div>
            {states.diffDate && (
              <div style={{ textAlign:"center" }}>
                <Text style={{ fontSize: 12, color: "#ccc", fontWeight: 300 }}>
                  Dernière synchro : {" "}
                </Text>
                <Text style={{ fontSize: 12, color: "#eee", fontWeight: 600 }}>
                  {states.diffDate.lastUpdateDate}
                </Text>
              </div>
            )}
          </Card>
          
        </Col>
      </Row>
    </div>
  );
};

export default Controle;
